import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogBody,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import RichTextEditor from '@/components/ui/rich-text-editor';
import { useSendQuickEmail, useSendCustomEmail } from '@/api/notifications/use-notifications';
import { useNotificationTemplates } from '@/api/notifications/use-notification-templates';
import toast from 'react-hot-toast';

export interface SendEmailNotificationDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    recipientEmail: string;
    recipientId: string;
    recipientType: 'customer' | 'lead';
    recipientName: string;
}

export const SendEmailNotificationDialog: React.FC<SendEmailNotificationDialogProps> = ({
    open,
    onOpenChange,
    recipientEmail,
    recipientId,
    recipientType,
    recipientName,
}) => {
    const [mode, setMode] = useState<'template' | 'custom'>('template');
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');

    const { templates, loading: templatesLoading } = useNotificationTemplates();
    const sendQuickEmail = useSendQuickEmail();
    const sendCustomEmail = useSendCustomEmail();

    // Filter templates to only show active email templates of type 'manual'
    const emailTemplates =
        templates?.filter(
            template =>
                template.is_active &&
                template.channels?.includes('email') &&
                template.type === 'manual'
        ) || [];

    // Get first custom template for language options in custom flow
    const firstCustomTemplate = templates?.find(
        template =>
            template.is_active && template.channels?.includes('email') && template.type === 'custom'
    );

    // Reset form when dialog opens
    useEffect(() => {
        if (open) {
            setMode('template');
            setSelectedTemplateId('');
            setSelectedLanguage(firstCustomTemplate?.default_language || 'en');
            setSubject('');
            setContent('');
        }
    }, [open, firstCustomTemplate]);

    // When a template is selected, populate subject and content
    useEffect(() => {
        if (selectedTemplateId) {
            const template = emailTemplates.find(t => t.id === selectedTemplateId);
            if (template) {
                // Reset to default language if template changes
                const defaultLang = template.default_language || 'en';
                if (!template.supported_languages?.includes(selectedLanguage)) {
                    setSelectedLanguage(defaultLang);
                }
            }
        }
    }, [selectedTemplateId, emailTemplates]);

    // When template or language changes, update subject and content
    useEffect(() => {
        if (selectedTemplateId) {
            const template = emailTemplates.find(t => t.id === selectedTemplateId);
            if (template) {
                // Get content for selected language and email channel
                const contentKey = `${selectedLanguage}_email`;
                const templateContent = template.content_variations?.[contentKey];

                console.log('Template selected:', {
                    templateId: selectedTemplateId,
                    templateName: template.name,
                    contentKey,
                    templateContent,
                    hasHtmlContent: !!templateContent?.html_content,
                    htmlContentLength: templateContent?.html_content?.length,
                    hasContent: !!templateContent?.content,
                    contentLength: templateContent?.content?.length,
                });

                if (templateContent) {
                    const newSubject = templateContent.subject || template.title || '';
                    // Prioritize html_content for rich HTML, fallback to plain content
                    const newContent =
                        templateContent.html_content ||
                        templateContent.content ||
                        template.content ||
                        '';

                    console.log('Setting values:', {
                        subject: newSubject,
                        contentLength: newContent.length,
                        contentPreview: newContent.substring(0, 200),
                    });

                    setSubject(newSubject);
                    setContent(newContent);
                } else {
                    // Fallback to template defaults
                    setSubject(template.title || '');
                    setContent(template.content || '');
                }
            }
        }
    }, [selectedTemplateId, selectedLanguage, emailTemplates]);

    const handleSend = async () => {
        if (!subject.trim() || !content.trim()) {
            toast.error('Please provide both subject and content');
            return;
        }

        try {
            if (mode === 'custom') {
                // Use custom email endpoint with customer_content
                await sendCustomEmail.mutateAsync({
                    recipient_id: recipientId,
                    recipient_type: recipientType,
                    subject: subject.trim(),
                    customer_content: content.trim(),
                    language: selectedLanguage,
                });
            } else {
                // Use quick email endpoint with template_id (manual template)
                await sendQuickEmail.mutateAsync({
                    recipient_id: recipientId,
                    recipient_type: recipientType,
                    subject: subject.trim(),
                    content: content.trim(),
                    template_id: selectedTemplateId || undefined,
                    language: selectedLanguage,
                });
            }

            toast.success(`Email sent successfully to ${recipientName}`);
            onOpenChange(false);
        } catch (error: any) {
            const message =
                error?.response?.data?.message || error?.message || 'Failed to send email';
            toast.error(message);
        }
    };

    const isFormValid = subject.trim() && content.trim();
    const isSending = sendQuickEmail.isPending || sendCustomEmail.isPending;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Send Email Notification</DialogTitle>
                    <DialogDescription>
                        Send an email notification to {recipientName} ({recipientEmail})
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="space-y-4 py-4 overflow-y-auto flex-1">
                    <Tabs
                        value={mode}
                        onValueChange={value => {
                            setMode(value as 'template' | 'custom');

                            // Clear subject and content when switching modes
                            setSubject('');
                            setContent('');

                            // Reset language and template selection based on mode
                            if (value === 'custom') {
                                // Clear template selection when switching to custom
                                setSelectedTemplateId('');
                                setSelectedLanguage(firstCustomTemplate?.default_language || 'en');
                            } else if (value === 'template') {
                                // When switching back to template mode, reset to default language
                                setSelectedLanguage('en');
                            }
                        }}
                    >
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="template">Use Template</TabsTrigger>
                            <TabsTrigger value="custom">Custom Message</TabsTrigger>
                        </TabsList>

                        <TabsContent value="template" className="space-y-4 mt-4">
                            <div className="space-y-2">
                                <Label htmlFor="template">Select Template</Label>
                                {templatesLoading ? (
                                    <div className="flex items-center justify-center p-4">
                                        <Loader2 className="h-6 w-6 animate-spin" />
                                    </div>
                                ) : emailTemplates.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">
                                        No email templates available. Switch to Custom Message to
                                        compose an email.
                                    </p>
                                ) : (
                                    <Select
                                        value={selectedTemplateId}
                                        onValueChange={setSelectedTemplateId}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Choose a template..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {emailTemplates.map(template => (
                                                <SelectItem key={template.id} value={template.id}>
                                                    {template.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            </div>

                            {selectedTemplateId && (
                                <>
                                    {/* Language Selector */}
                                    <div className="space-y-2">
                                        <Label htmlFor="language">Language</Label>
                                        <Select
                                            value={selectedLanguage}
                                            onValueChange={setSelectedLanguage}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {emailTemplates
                                                    .find(t => t.id === selectedTemplateId)
                                                    ?.supported_languages?.map(lang => (
                                                        <SelectItem key={lang} value={lang}>
                                                            {lang.toUpperCase()}
                                                        </SelectItem>
                                                    )) || <SelectItem value="en">EN</SelectItem>}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-muted-foreground">
                                            Select the language for the template preview
                                        </p>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="subject">Subject (Preview)</Label>
                                        <Input
                                            id="subject"
                                            value={subject}
                                            readOnly
                                            disabled
                                            placeholder="Email subject"
                                            className="bg-muted"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="content">Content (Preview)</Label>
                                        <div className="border rounded-md p-3 bg-muted min-h-[200px] overflow-auto">
                                            <div dangerouslySetInnerHTML={{ __html: content }} />
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            This is a preview of the template. No editing allowed
                                            for manual templates.
                                        </p>
                                    </div>
                                </>
                            )}
                        </TabsContent>

                        <TabsContent value="custom" className="space-y-4 mt-4">
                            {/* Language Selector for Custom Template */}
                            {firstCustomTemplate && firstCustomTemplate.supported_languages && (
                                <div className="space-y-2">
                                    <Label htmlFor="custom-language">Language</Label>
                                    <Select
                                        value={selectedLanguage}
                                        onValueChange={setSelectedLanguage}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {firstCustomTemplate.supported_languages.map(lang => (
                                                <SelectItem key={lang} value={lang}>
                                                    {lang.toUpperCase()}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-muted-foreground">
                                        Select the language for your custom email
                                    </p>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="custom-subject">Subject</Label>
                                <Input
                                    id="custom-subject"
                                    value={subject}
                                    onChange={e => setSubject(e.target.value)}
                                    placeholder="Email subject"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="custom-content">Content</Label>
                                <RichTextEditor
                                    value={content}
                                    onChange={setContent}
                                    placeholder="Write your message here..."
                                />
                            </div>
                        </TabsContent>
                    </Tabs>
                </DialogBody>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isSending}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleSend} disabled={!isFormValid || isSending}>
                        {isSending ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Sending...
                            </>
                        ) : (
                            'Send Email'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
